/**
 * Elementtyp bzw. Pruefbereichs-Schluessel (siehe Backend
 * `stammdaten/pruefbereicheKatalog.ts`). 'TÜR'/'SCHOTT' sind der
 * Altbestand mit Umlaut, die vier neuen Werte sind ASCII-Schluessel wie
 * im Pruefbereichs-Stammdatensatz -- Phase 6, PLAN_V4_SAAS_ROADMAP.
 */
export type ElementTyp = 'TÜR' | 'SCHOTT' | 'FEUERLOESCHER' | 'RWA' | 'BMA' | 'SPRINKLER';

/**
 * Bildet `elemente.typ` auf `pruefbereiche.schluessel` ab -- Frontend-
 * Gegenstueck zu `schluesselFuerTyp()` in
 * `backend/src/stammdaten/pruefbereicheKatalog.ts`.
 *
 * Nur 'TÜR' braucht die Abbildung (Umlaut in `typ`, ASCII-Schluessel im
 * Katalog); alle anderen Werte -- inklusive der vier neuen Bereiche --
 * schreiben den Schluessel bereits direkt in `typ`.
 */
export function schluesselFuerElementTyp(typ: string | null | undefined): string | null {
  if (!typ) return null;
  const wert = typ.trim().toUpperCase();
  if (wert === 'TÜR') return 'TUER';
  return wert;
}

export interface Element {
  id?: number;
  stockwerk_id: number;
  element_id_intern?: string;
  element_id_kunde?: string;
  typ: ElementTyp;
  status?: string;
  barcode?: string;
  raum?: string;
  technische_daten?: Record<string, any>;
  pruef_intervall?: string;
  letzte_pruefung?: Date;
  naechste_pruefung?: Date;
  erstellt_am?: Date;
  aktualisiert_am?: Date;
}

export interface ElementWithDetails extends Element {
  stockwerk_bezeichnung?: string;
  gebaeude_bezeichnung?: string;
  gebaeude_id?: number;
  standort_name?: string;
  standort_id?: number;
  kunde_name?: string;
  kunde_id?: number;
  maengel_count?: number;
  fotos_count?: number;
}

export interface ElementFilter {
  stockwerkId?: number;
  gebaeudeId?: number;
  standortId?: number;
  kundeId?: number;
  typ?: ElementTyp;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ElementsResponse {
  data: ElementWithDetails[];
  total: number;
}

export type ElementStatus = 'i.O.' | 'n.i.O.' | 'NICHT_GEPRUEFT' | 'IN_PRUEFUNG' | 'GESPERRT';

export const STATUS_LABELS: Record<string, { label: string; color: string; bgColor: string }> = {
  'i.O.': { label: 'In Ordnung', color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
  'n.i.O.': { label: 'Nicht i.O.', color: 'text-red-700', bgColor: 'bg-red-100' },
  'NICHT_GEPRUEFT': { label: 'Nicht geprüft', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  'IN_PRUEFUNG': { label: 'In Prüfung', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  'GESPERRT': { label: 'Gesperrt', color: 'text-amber-700', bgColor: 'bg-amber-100' },
};

export const TYP_LABELS: Record<string, { label: string; color: string; bgColor: string }> = {
  'TÜR': { label: 'Brandschutztür', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  'SCHOTT': { label: 'Brandschott', color: 'text-indigo-700', bgColor: 'bg-indigo-100' },
  'FEUERLOESCHER': { label: 'Feuerlöscher', color: 'text-red-700', bgColor: 'bg-red-100' },
  'RWA': { label: 'RWA-Anlage', color: 'text-sky-700', bgColor: 'bg-sky-100' },
  'BMA': { label: 'Brandmeldeanlage', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  'SPRINKLER': { label: 'Sprinkleranlage', color: 'text-cyan-700', bgColor: 'bg-cyan-100' },
};
